import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import { auth as fbAuth } from "../firebase";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getAuth() {
  try { return JSON.parse(localStorage.getItem("auth") || "{}"); } catch { return {}; }
}

async function authedFetch(path, opts = {}) {
  const token = await fbAuth.currentUser?.getIdToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
}

async function reauth(password) {
  const user = fbAuth.currentUser;
  if (!user?.email) throw new Error("no-user");
  const cred = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, cred);
}

async function syncLocalAuth(overrides = {}) {
  try {
    const token = await fbAuth.currentUser?.getIdToken(true);
    if (!token) return;
    const res = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = await res.json();
    const user = data.user ?? data;
    const prev = getAuth();
    localStorage.setItem("auth", JSON.stringify({ ...prev, ...user, token, ...overrides }));
  } catch {}
}

function firebaseMsg(err, t) {
  const code = err?.code || "";
  if (code === "auth/wrong-password" || code === "auth/invalid-credential")
    return t("accountSettings.errors.wrongPassword");
  if (code === "auth/weak-password")        return t("accountSettings.errors.weakPassword");
  if (code === "auth/email-already-in-use") return t("accountSettings.errors.emailInUse");
  if (code === "auth/invalid-email")        return t("accountSettings.errors.invalidEmail");
  if (code === "auth/requires-recent-login") return t("accountSettings.errors.requiresRecentLogin");
  if (code === "auth/too-many-requests")    return t("accountSettings.errors.tooManyRequests");
  return t("accountSettings.errors.generic");
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────
const inputCls =
  "w-full rounded-xl border border-white/10 bg-black/20 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#7269e3] text-white placeholder-white/30 transition";

function Label({ children, hint }) {
  return (
    <div className="mb-1.5">
      <span className="text-sm font-medium text-white/85">{children}</span>
      {hint && <span className="ml-2 text-xs text-white/40">{hint}</span>}
    </div>
  );
}

function Feedback({ msg, type }) {
  if (!msg) return null;
  return (
    <p className={`flex items-center gap-2 text-sm ${type === "error" ? "text-red-300" : "text-emerald-300"}`}>
      <span>{type === "error" ? "⚠" : "✓"}</span>
      {msg}
    </p>
  );
}

function PrimaryBtn({ loading, children, danger, ...props }) {
  const base = danger
    ? "bg-red-500/90 hover:bg-red-500 text-white"
    : "bg-[#7269e3] hover:brightness-110 text-white";
  return (
    <button
      type={props.type || "submit"}
      disabled={loading || props.disabled}
      className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition disabled:opacity-40 ${base}`}
      {...props}
    >
      {loading ? <span className="inline-block w-4 text-center">…</span> : children}
    </button>
  );
}

function GhostBtn({ children, ...props }) {
  return (
    <button
      type="button"
      className="rounded-xl border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:bg-white/8 transition disabled:opacity-40"
      {...props}
    >
      {children}
    </button>
  );
}

// Card with divider sections
function Card({ children }) {
  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
      {children}
    </div>
  );
}

function CardSection({ children, last }) {
  return (
    <div className={`px-6 py-5 ${!last ? "border-b border-white/8" : ""}`}>
      {children}
    </div>
  );
}

function CardHeader({ title, subtitle }) {
  return (
    <CardSection>
      <h2 className="font-unbounded text-base font-semibold">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-white/50">{subtitle}</p>}
    </CardSection>
  );
}

// ─── Delete modal (3-step) ───────────────────────────────────────────────────
function DeleteModal({ open, onClose }) {
  const { t } = useTranslation();
  const [step,     setStep]     = useState(1); // 1=warn, 2=password, 3=done
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  function reset() { setStep(1); setPassword(""); setError(""); setLoading(false); }
  function handleClose() { reset(); onClose(); }

  async function confirm() {
    setError(""); setLoading(true);
    try {
      await reauth(password);
      await authedFetch("/api/me", { method: "DELETE" });
      await deleteUser(fbAuth.currentUser);
      localStorage.removeItem("auth");
      localStorage.removeItem("isAuthed");
      setStep(3);
    } catch (err) {
      setError(firebaseMsg(err, t));
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4 pb-6 sm:pb-0"
      onClick={step < 3 ? handleClose : undefined}>
      <div
        className="w-full max-w-md rounded-2xl bg-[#2a2f3e] ring-1 ring-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step 1 — warning */}
        {step === 1 && (
          <>
            <div className="px-6 pt-6 pb-4">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15 ring-1 ring-red-500/30">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">{t("accountSettings.danger.modalTitle")}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{t("accountSettings.danger.modalBody")}</p>
              <ul className="mt-3 space-y-1.5">
                {["deleteItem1", "deleteItem2", "deleteItem3"].map((k) => (
                  <li key={k} className="flex items-center gap-2 text-sm text-white/55">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-400/60" />
                    {t(`accountSettings.danger.${k}`)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-3 border-t border-white/8 px-6 py-4">
              <button
                onClick={() => setStep(2)}
                className="rounded-xl bg-red-500/90 hover:bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition"
              >
                {t("accountSettings.danger.proceedBtn")}
              </button>
              <GhostBtn onClick={handleClose}>{t("accountSettings.danger.cancelBtn")}</GhostBtn>
            </div>
          </>
        )}

        {/* Step 2 — password confirmation */}
        {step === 2 && (
          <>
            <div className="px-6 pt-6 pb-4">
              <h3 className="text-lg font-semibold text-white">{t("accountSettings.danger.step2Title")}</h3>
              <p className="mt-2 text-sm text-white/60">{t("accountSettings.danger.step2Body")}</p>
              <div className="mt-4">
                <Label>{t("accountSettings.password.current")}</Label>
                <input
                  type="password"
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && password && confirm()}
                  disabled={loading}
                />
              </div>
              {error && <p className="mt-3 flex items-center gap-2 text-sm text-red-300"><span>⚠</span>{error}</p>}
            </div>
            <div className="flex gap-3 border-t border-white/8 px-6 py-4">
              <PrimaryBtn loading={loading} danger disabled={!password} onClick={confirm} type="button">
                {t("accountSettings.danger.confirmBtn")}
              </PrimaryBtn>
              <GhostBtn onClick={handleClose} disabled={loading}>{t("accountSettings.danger.cancelBtn")}</GhostBtn>
            </div>
          </>
        )}

        {/* Step 3 — done */}
        {step === 3 && (
          <div className="px-6 py-8 text-center">
            <div className="mb-4 text-3xl">👋</div>
            <h3 className="text-lg font-semibold">{t("accountSettings.danger.doneTitle")}</h3>
            <p className="mt-2 text-sm text-white/55">{t("accountSettings.danger.doneBody")}</p>
            <button
              onClick={() => { window.location.href = "/"; }}
              className="mt-6 rounded-xl bg-[#7269e3] px-6 py-2.5 text-sm font-semibold hover:brightness-110 transition"
            >
              {t("accountSettings.danger.doneBtn")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AccountSettings() {
  const { t, i18n } = useTranslation();
  const fileRef = useRef(null);

  const storedAuth   = getAuth();
  const currentEmail = fbAuth.currentUser?.email ?? storedAuth.email ?? "";
  const lang = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("uk") ? "uk" : "en";

  // ── Personal info ────────────────────────────────────────────────────────────
  const [name,        setName]        = useState(storedAuth.name || "");
  const [avatarUrl,   setAvatarUrl]   = useState("");
  // Tracks whether the current avatarUrl actually failed to load as an image.
  // Previously the onError handler just hid the broken <img> in place
  // (display:none) without swapping back to the default-avatar placeholder,
  // so a stale/dead avatarUrl left the whole avatar slot blank instead of
  // showing the standard fallback icon.
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoStatus,  setInfoStatus]  = useState({ msg: "", type: "" });
  const [uploadingAv, setUploadingAv] = useState(false);

  useEffect(() => {
    authedFetch("/api/me/profile")
      .then((r) => r.json())
      .then((d) => {
        if (d.profile?.displayName) setName(d.profile.displayName);
        if (d.profile?.avatarUrl) {
          setAvatarUrl(d.profile.avatarUrl);
          setAvatarLoadFailed(false);
        }
      })
      .catch(() => {});
  }, []);

  function absUrl(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${API_BASE}${url}`;
  }

  // Downscale + re-encode the picked image to a small JPEG on the client.
  // iPhone photos are typically 3–8 MB (over the backend's 5 MB multer cap)
  // and can arrive as HEIC (which the backend's JPG/PNG/WebP filter rejects),
  // so uploads from iOS silently failed. Re-encoding through a canvas fixes
  // both: any browser-decodable format becomes a ~100 KB JPEG.
  async function downscaleAvatar(file, maxDim = 512, quality = 0.85) {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (!blob) return null;
      return new File([blob], "avatar.jpg", { type: "image/jpeg" });
    } catch {
      return null;
    }
  }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingAv(true);
    setInfoStatus({ msg: "", type: "" });
    try {
      const processed = await downscaleAvatar(file);
      const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
      const MAX_BYTES = 5 * 1024 * 1024; // keep in sync with backend multer limit
      // Prefer the re-encoded JPEG; fall back to the original only if it
      // would pass the backend's own checks anyway.
      const toSend =
        processed ||
        (ACCEPTED.includes(file.type) && file.size <= MAX_BYTES ? file : null);
      if (!toSend) throw new Error("unsupported-image");
      const fd = new FormData();
      fd.append("avatar", toSend, toSend.name || "avatar.jpg");
      const token = await fbAuth.currentUser?.getIdToken();
      if (!token) throw new Error("no-auth-token");
      const res   = await fetch(`${API_BASE}/api/me/avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      // The response may be non-JSON (proxy/error pages) — don't let .json()
      // mask the real HTTP status in that case.
      let data = null;
      try { data = await res.json(); } catch { /* non-JSON body */ }
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setAvatarUrl(data.avatarUrl);
      setAvatarLoadFailed(false);
      // Navbar.jsx keeps its own copy of avatarUrl (it only re-fetches on auth/route
      // changes), so without this it keeps showing the stale placeholder/initials
      // until the user navigates somewhere else. Broadcast the change so it can
      // update itself immediately, in the same tab, with no route change needed.
      window.dispatchEvent(
        new CustomEvent("profio:avatar-updated", { detail: { avatarUrl: data.avatarUrl } })
      );
      setInfoStatus({ msg: t("accountSettings.info.avatarSaved"), type: "success" });
    } catch (err) {
      // Surface the underlying reason (server error code / HTTP status /
      // client-side decode failure) — a bare generic message made the iOS
      // upload issue impossible to diagnose from user reports.
      const detail = err?.message ? ` (${err.message})` : "";
      setInfoStatus({ msg: `${t("accountSettings.errors.generic")}${detail}`, type: "error" });
    } finally { setUploadingAv(false); }
  }

  async function handleSaveName(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setInfoLoading(true);
    setInfoStatus({ msg: "", type: "" });
    try {
      const token = await fbAuth.currentUser?.getIdToken();
      const res   = await fetch(`${API_BASE}/api/me/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ displayName: name.trim() }),
      });
      if (!res.ok) throw new Error();
      const prev = getAuth();
      localStorage.setItem("auth", JSON.stringify({ ...prev, name: name.trim() }));
      setInfoStatus({ msg: t("accountSettings.info.nameSaved"), type: "success" });
    } catch {
      setInfoStatus({ msg: t("accountSettings.errors.generic"), type: "error" });
    } finally { setInfoLoading(false); }
  }

  // ── Password ─────────────────────────────────────────────────────────────────
  const [pw,        setPw]        = useState({ current: "", next: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwStatus,  setPwStatus]  = useState({ msg: "", type: "" });

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwStatus({ msg: "", type: "" });
    if (pw.next !== pw.confirm)
      return setPwStatus({ msg: t("accountSettings.errors.passwordMismatch"), type: "error" });
    if (pw.next.length < 6)
      return setPwStatus({ msg: t("accountSettings.errors.weakPassword"), type: "error" });
    setPwLoading(true);
    try {
      await reauth(pw.current);
      await updatePassword(fbAuth.currentUser, pw.next);
      setPw({ current: "", next: "", confirm: "" });
      setPwStatus({ msg: t("accountSettings.password.success"), type: "success" });
    } catch (err) {
      setPwStatus({ msg: firebaseMsg(err, t), type: "error" });
    } finally { setPwLoading(false); }
  }

  // ── Email ────────────────────────────────────────────────────────────────────
  const [emailForm,    setEmailForm]    = useState({ current: "", newEmail: "" });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus,  setEmailStatus]  = useState({ msg: "", type: "" });

  async function handleChangeEmail(e) {
    e.preventDefault();
    setEmailStatus({ msg: "", type: "" });
    setEmailLoading(true);
    try {
      await reauth(emailForm.current);
      await updateEmail(fbAuth.currentUser, emailForm.newEmail.trim());
      await syncLocalAuth({ email: emailForm.newEmail.trim() });
      setEmailForm({ current: "", newEmail: "" });
      setEmailStatus({ msg: t("accountSettings.email.success"), type: "success" });
    } catch (err) {
      setEmailStatus({ msg: firebaseMsg(err, t), type: "error" });
    } finally { setEmailLoading(false); }
  }

  // ── Language ─────────────────────────────────────────────────────────────────
  const [langSaved, setLangSaved] = useState(false);
  async function switchLang(l) {
    await i18n.changeLanguage(l);
    localStorage.setItem("i18nextLng", l);
    setLangSaved(true);
    setTimeout(() => setLangSaved(false), 2500);
  }

  // ── Delete ────────────────────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-2xl px-4 pt-10 pb-28">
        <div className="h-12" />

        {/* Page header */}
        <div className="mb-8">
          <h1 className="font-unbounded text-2xl font-extrabold md:text-3xl">
            {t("accountSettings.title")}
          </h1>
          <p className="mt-2 text-sm text-white/50">{t("accountSettings.subtitle")}</p>
        </div>

        <div className="space-y-5">

          {/* ── 1. Personal info ── */}
          <Card>
            <CardHeader
              title={t("accountSettings.info.title")}
              subtitle={t("accountSettings.info.subtitle")}
            />
            <CardSection>
              {/* Avatar */}
              <div className="flex items-center gap-4 mb-5">
                <div className="relative shrink-0">
                  {avatarUrl && !avatarLoadFailed ? (
                    <img
                      crossOrigin="anonymous"
                      src={absUrl(avatarUrl)}
                      onError={() => setAvatarLoadFailed(true)}
                      alt="avatar"
                      className="h-16 w-16 rounded-full object-cover ring-2 ring-white/15"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-white/10 ring-2 ring-white/15 flex items-center justify-center text-white/35">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                        <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                  {uploadingAv && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                      <span className="text-xs">…</span>
                    </div>
                  )}
                </div>
                <div>
                  {/* Native <label htmlFor> click-forwarding instead of a button
                      calling fileRef.current.click() — iOS Safari (and some
                      Android WebViews) silently refuse to open the native
                      photo picker when a hidden file input is triggered via
                      a programmatic .click() from a separate element; a real
                      <label> association is the only cross-mobile-reliable way. */}
                  <input
                    ref={fileRef}
                    id="avatarFileInput"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                    style={{ position: "absolute", width: 0, height: 0, opacity: 0, overflow: "hidden" }}
                    onChange={handleAvatarChange}
                    disabled={uploadingAv}
                  />
                  <label
                    htmlFor="avatarFileInput"
                    className={`inline-block rounded-xl border border-white/15 bg-white/6 px-4 py-2 text-sm hover:bg-white/12 transition cursor-pointer ${
                      uploadingAv ? "opacity-50 pointer-events-none" : ""
                    }`}
                  >
                    {t("accountSettings.info.changeAvatar")}
                  </label>
                  <p className="mt-1.5 text-xs text-white/35">
                    {t("accountSettings.info.avatarHint", { defaultValue: "JPG, PNG, WebP або HEIC" })}
                  </p>
                </div>
              </div>
              {/* Name form */}
              <form onSubmit={handleSaveName} className="space-y-4">
                <div>
                  <Label hint={t("accountSettings.info.nameHint")}>{t("accountSettings.info.name")}</Label>
                  <input className={inputCls} value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("accountSettings.info.namePh")}
                    required disabled={infoLoading} />
                </div>
                <div>
                  <Label>{t("accountSettings.info.emailLabel")}</Label>
                  <input className={`${inputCls} opacity-45 cursor-not-allowed`} value={currentEmail} readOnly />
                  <p className="mt-1 text-xs text-white/35">{t("accountSettings.info.emailHint")}</p>
                </div>
                <div className="flex items-center gap-4 pt-1">
                  <PrimaryBtn loading={infoLoading}>{t("accountSettings.info.save")}</PrimaryBtn>
                  <Feedback msg={infoStatus.msg} type={infoStatus.type} />
                </div>
              </form>
            </CardSection>
          </Card>

          {/* ── 2. Security ── */}
          <Card>
            <CardHeader
              title={t("accountSettings.security.title")}
              subtitle={t("accountSettings.security.subtitle")}
            />
            {/* Change password */}
            <CardSection>
              <p className="mb-4 text-sm font-medium text-white/70">{t("accountSettings.password.title")}</p>
              <form onSubmit={handleChangePassword} className="space-y-3">
                <div>
                  <Label>{t("accountSettings.password.current")}</Label>
                  <input type="password" className={inputCls} value={pw.current}
                    onChange={(e) => setPw((f) => ({ ...f, current: e.target.value }))}
                    placeholder="••••••••" required disabled={pwLoading} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>{t("accountSettings.password.new")}</Label>
                    <input type="password" className={inputCls} value={pw.next}
                      onChange={(e) => setPw((f) => ({ ...f, next: e.target.value }))}
                      placeholder="••••••••" required disabled={pwLoading} />
                  </div>
                  <div>
                    <Label>{t("accountSettings.password.confirm")}</Label>
                    <input type="password" className={inputCls} value={pw.confirm}
                      onChange={(e) => setPw((f) => ({ ...f, confirm: e.target.value }))}
                      placeholder="••••••••" required disabled={pwLoading} />
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-1">
                  <PrimaryBtn loading={pwLoading}>{t("accountSettings.password.submit")}</PrimaryBtn>
                  <Feedback msg={pwStatus.msg} type={pwStatus.type} />
                </div>
              </form>
            </CardSection>
            {/* Change email */}
            <CardSection last>
              <p className="mb-4 text-sm font-medium text-white/70">{t("accountSettings.email.title")}</p>
              <p className="mb-3 text-xs text-white/40">
                {t("accountSettings.email.subtitle", { current: currentEmail })}
              </p>
              <form onSubmit={handleChangeEmail} className="space-y-3">
                <div>
                  <Label hint={t("accountSettings.email.passwordHint")}>{t("accountSettings.password.current")}</Label>
                  <input type="password" className={inputCls} value={emailForm.current}
                    onChange={(e) => setEmailForm((f) => ({ ...f, current: e.target.value }))}
                    placeholder="••••••••" required disabled={emailLoading} />
                </div>
                <div>
                  <Label>{t("accountSettings.email.newEmail")}</Label>
                  <input type="email" className={inputCls} value={emailForm.newEmail}
                    onChange={(e) => setEmailForm((f) => ({ ...f, newEmail: e.target.value }))}
                    placeholder={t("accountSettings.email.placeholder")} required disabled={emailLoading} />
                </div>
                <div className="flex items-center gap-4 pt-1">
                  <PrimaryBtn loading={emailLoading}>{t("accountSettings.email.submit")}</PrimaryBtn>
                  <Feedback msg={emailStatus.msg} type={emailStatus.type} />
                </div>
              </form>
            </CardSection>
          </Card>

          {/* ── 3. Language ── */}
          <Card>
            <CardHeader
              title={t("accountSettings.language.title")}
              subtitle={t("accountSettings.language.subtitle")}
            />
            <CardSection last>
              <div className="flex items-center gap-3">
                {[
                  { code: "uk", label: "Українська", flag: "🇺🇦" },
                  { code: "en", label: "English",    flag: "🇬🇧" },
                ].map(({ code, label, flag }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => switchLang(code)}
                    className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                      lang === code
                        ? "bg-[#7269e3]/25 text-white ring-1 ring-[#7269e3]/50"
                        : "border border-white/10 text-white/55 hover:bg-white/8"
                    }`}
                  >
                    <span>{flag}</span>
                    {label}
                    {lang === code && (
                      <span className="ml-1 text-[#a89ef5]">✓</span>
                    )}
                  </button>
                ))}
              </div>
              {langSaved && (
                <p className="mt-3 flex items-center gap-2 text-sm text-emerald-300">
                  <span>✓</span> {t("accountSettings.language.saved")}
                </p>
              )}
            </CardSection>
          </Card>

          {/* ── 4. Danger zone ── */}
          <Card>
            <CardSection last>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-red-300">{t("accountSettings.danger.deleteBtn")}</p>
                  <p className="mt-1 text-xs text-white/45 max-w-sm leading-relaxed">
                    {t("accountSettings.danger.deleteHint")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  className="shrink-0 rounded-xl border border-red-500/30 bg-red-500/8 px-4 py-2 text-sm font-medium text-red-300 hover:bg-red-500/15 transition"
                >
                  {t("accountSettings.danger.deleteTrigger")}
                </button>
              </div>
            </CardSection>
          </Card>

        </div>
      </div>

      <DeleteModal open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </main>
  );
}
